import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowDownRight, IconArrowUpRight, IconMinus } from '@tabler/icons-react';
import styles from '../ReportMetrics.module.scss';
import detailStyles from '../widgets/Detail.module.scss';
import { KpiScope } from '../../../api/model';
import { HistoryPoint, KPI, labelOf, numeric } from '../intelligence';
import {
  Change,
  HistoryIndex,
  changeOver,
  directionOf,
  isOpenAction,
  isoDay,
  scopedHistory,
  signed,
  withinDays,
} from '../detail';
import type { ReportViewProps } from './viewProps';
import type { ReportTab } from '../ReportMetrics';
import { deltaFromChange, formatDay, pct, scoreStatus } from './tiles';
import ChartCard from '../widgets/ChartCard';
import MetricTile from '../widgets/MetricTile';
import MultiLineChart from '../widgets/MultiLineChart';
import DataTable from '../widgets/DataTable';
import Segmented from '../widgets/Segmented';
import Sparkline from '../widgets/Sparkline';
import ActionsHint from '../widgets/ActionsHint';

/** The comparison point of the tiles and the trend table - the mockup's "vs. letzter Monat". */
const COMPARE_DAYS = 30;

/** A component that moves the other way to its dimension when it rises. */
interface Component {
  key: string;
  lowerIsBetter?: boolean;
}

/**
 * The five management KPIs, each with the components it is computed from - so
 * "what changed" can name the component that moved most, a cause read straight
 * off Qlario's own data rather than guessed at (spec: "keine spekulativen
 * Ursachen").
 */
const DIMENSIONS: { key: string; labelKey: string; color: string; tab: ReportTab; components: Component[] }[] = [
  {
    key: KPI.projectHealthScore,
    labelKey: 'health',
    color: '#2D78DB',
    tab: 'overview',
    components: [
      { key: KPI.readinessScore },
      { key: KPI.executionScore },
      { key: KPI.qualityScore },
      { key: KPI.dependencyScore },
    ],
  },
  {
    key: KPI.readinessScore,
    labelKey: 'readiness',
    color: '#1BAF7A',
    tab: 'readiness',
    components: [
      { key: KPI.planningReadinessLo },
      { key: KPI.ownershipReadiness },
      { key: KPI.structureReadiness },
      { key: KPI.qualityReadiness },
      { key: KPI.scheduleConsistency },
      { key: KPI.projectPhaseDataReadiness },
      { key: KPI.projectLoDataReadiness },
    ],
  },
  {
    key: KPI.executionScore,
    labelKey: 'execution',
    color: '#F59E0B',
    tab: 'execution',
    components: [
      { key: KPI.projectProgressPerformance },
      { key: KPI.scheduleReliability },
      { key: KPI.criticalDeliveryPerformance },
      { key: KPI.overdueDeliverableRate, lowerIsBetter: true },
      { key: KPI.overduePhaseRate, lowerIsBetter: true },
    ],
  },
  {
    key: KPI.qualityScore,
    labelKey: 'quality',
    color: '#8B5CF6',
    tab: 'quality',
    components: [
      { key: KPI.qaCoverage },
      { key: KPI.projectQaCompletion },
      { key: KPI.qaTimeliness },
      { key: KPI.delayedCheckpointRate, lowerIsBetter: true },
    ],
  },
  {
    key: KPI.dependencyScore,
    labelKey: 'dependency',
    color: '#06B6D4',
    tab: 'dependencies',
    components: [
      { key: KPI.dependencyIntegrity },
      { key: KPI.hardDependencyViolationRate, lowerIsBetter: true },
      { key: KPI.overduePredecessorRate, lowerIsBetter: true },
      { key: KPI.parallelDependencyRiskRate, lowerIsBetter: true },
    ],
  },
];

const PHASE_COLORS = ['#2D78DB', '#1BAF7A', '#F59E0B', '#8B5CF6', '#06B6D4', '#EC835A', '#D03B3B', '#64748B'];

type Range = '84' | '182' | '365';
type PhaseRange = '28' | Range;
/** Below this many measured days the phase trends say the history is still young. */
const FEW_POINTS = 5;
type ChangeFilter = 'all' | 'positive' | 'negative';
type PhaseTrend = 'progress' | 'qa' | 'status';

/** The value a series had on the last day at or before `date`. */
function valueOn(points: HistoryPoint[] | undefined, date: string): number | undefined {
  let value: number | undefined;
  for (const point of points ?? []) {
    if (point.date <= date) value = point.value;
  }
  return value;
}

interface SignificantChange {
  key: string;
  labelKey: string;
  tab: ReportTab;
  change: Change;
  cause?: { key: string; delta: number };
}

function significantChanges(index: HistoryIndex, current: (key: string) => number | undefined, days: number) {
  const result: SignificantChange[] = [];
  for (const dimension of DIMENSIONS) {
    const change = changeOver(index.get(dimension.key), current(dimension.key), days);
    if (!change || Math.round(change.delta) === 0) continue;
    // The component that moved most in the same direction as its dimension.
    let cause: SignificantChange['cause'];
    for (const component of dimension.components) {
      const componentChange = changeOver(index.get(component.key), current(component.key), days);
      if (!componentChange) continue;
      const effect = component.lowerIsBetter ? -componentChange.delta : componentChange.delta;
      if (Math.sign(effect) !== Math.sign(change.delta)) continue;
      if (!cause || Math.abs(componentChange.delta) > Math.abs(cause.delta)) {
        cause = { key: component.key, delta: componentChange.delta };
      }
    }
    result.push({ key: dimension.key, labelKey: dimension.labelKey, tab: dimension.tab, change, cause });
  }
  return result.sort((a, b) => Math.abs(b.change.delta) - Math.abs(a.change.delta));
}

/**
 * Trends (Detailmasken §6): what changed, in which direction, and which
 * dimension or phase caused it. Historical only - no trend index and no
 * forecast in V1.
 */
const Trends: React.FC<ReportViewProps> = ({ actionPoints, history, historyIndex, index, phaseNames, openTab }) => {
  const { t } = useTranslation(['templates', 'gantt']);
  const [range, setRange] = useState<Range>('365');
  const [changeFilter, setChangeFilter] = useState<ChangeFilter>('all');
  const [phaseTrend, setPhaseTrend] = useState<PhaseTrend>('progress');
  const [phaseId, setPhaseId] = useState<number | 'all'>('all');
  const [phaseRange, setPhaseRange] = useState<PhaseRange>('84');

  const days = Number(range);
  const phaseDays = Number(phaseRange);
  const current = (key: string) => numeric(index, key);
  const kpiName = (key: string) =>
    t(`report.detail.kpiName.${key.replace(/\./g, '_')}`, { defaultValue: labelOf(index, key, key) });

  const changes = useMemo(
    () => significantChanges(historyIndex, (key) => numeric(index, key), days),
    [historyIndex, index, days],
  );
  const shownChanges = changes.filter((c) =>
    changeFilter === 'all' ? true : changeFilter === 'positive' ? c.change.delta > 0 : c.change.delta < 0,
  );

  const today = isoDay(new Date());
  const compareDate = isoDay(new Date(Date.now() - COMPARE_DAYS * 86_400_000));

  const phaseSeries = useMemo(() => {
    if (phaseTrend === 'status') {
      // TR-03: the share of phases finished vs. delayed, per day
      const statuses = scopedHistory(history, KPI.phaseStatus, KpiScope.PHASE);
      const byDate = new Map<string, { finished: number; delayed: number; total: number }>();
      for (const { categorical } of statuses.values()) {
        for (const point of categorical) {
          const day = byDate.get(point.date) ?? { finished: 0, delayed: 0, total: 0 };
          day.total++;
          if (point.value === 'FINISHED') day.finished++;
          if (point.value === 'DELAYED') day.delayed++;
          byDate.set(point.date, day);
        }
      }
      const dates = [...byDate.keys()].sort();
      return [
        {
          name: t('report.detail.trends.finished'),
          color: '#0CA30C',
          points: dates.map((date) => ({ date, value: (byDate.get(date)!.finished / byDate.get(date)!.total) * 100 })),
        },
        {
          name: t('report.detail.trends.delayed'),
          color: '#D03B3B',
          points: dates.map((date) => ({ date, value: (byDate.get(date)!.delayed / byDate.get(date)!.total) * 100 })),
        },
      ].map((s) => ({ ...s, points: withinDays(s.points, phaseDays) }));
    }
    const key = phaseTrend === 'progress' ? KPI.phaseProgress : KPI.phaseQaCompletion;
    const byPhase = scopedHistory(history, key, KpiScope.PHASE);
    return [...phaseNames.entries()]
      .filter(([id]) => phaseId === 'all' || id === phaseId)
      .map(([id, name], i) => ({
        name,
        color: PHASE_COLORS[i % PHASE_COLORS.length],
        points: withinDays(byPhase.get(id)?.numeric, phaseDays),
      }))
      .filter((s) => s.points.length > 0);
  }, [history, phaseTrend, phaseId, phaseNames, phaseDays, t]);
  const phaseDates = useMemo(
    () => [...new Set(phaseSeries.flatMap((s) => s.points.map((p) => p.date)))].sort(),
    [phaseSeries],
  );

  // the one rule that watches a trend (AP-PH-09) - its open actions are the "negative trends" ones
  const trendActions = (actionPoints ?? []).filter(
    (p) => isOpenAction(p) && p.ruleKey === 'ap.ph09_phase_qa_deteriorating',
  ).length;

  return (
    <div className={styles.dashboard}>
      <div className={styles.tileRow}>
        {DIMENSIONS.map((dimension) => {
          const value = current(dimension.key);
          const points = historyIndex.get(dimension.key);
          return (
            <MetricTile
              key={dimension.key}
              label={t(`report.detail.trends.dimension.${dimension.labelKey}`)}
              value={pct(value)}
              delta={deltaFromChange(t, changeOver(points, value, COMPARE_DAYS))}
              status={scoreStatus(t, value)}
              footer={<Sparkline points={withinDays(points, 90)} color={dimension.color} />}
              onClick={() => openTab?.(dimension.tab)}
            />
          );
        })}
      </div>

      <div className={styles.chartGrid}>
        <div className={styles.spanTwo}>
          <ChartCard
            title={t('report.detail.trends.development')}
            subtitle={t('report.detail.trends.developmentHint')}
            action={
              <Segmented<Range>
                value={range}
                onChange={setRange}
                options={[
                  { value: '84', label: t('report.detail.range.weeks12') },
                  { value: '182', label: t('report.detail.range.months6') },
                  { value: '365', label: t('report.detail.range.months12') },
                ]}
              />
            }
          >
            <MultiLineChart
              series={DIMENSIONS.map((dimension) => ({
                name: t(`report.detail.trends.dimension.${dimension.labelKey}`),
                color: dimension.color,
                points: withinDays(historyIndex.get(dimension.key), days),
              }))}
            />
          </ChartCard>
        </div>

        <ChartCard title={t('report.detail.trends.analysis')} subtitle={t('report.detail.trends.analysisHint')} fitContent>
          <DataTable
            rows={DIMENSIONS}
            rowKey={(row) => row.key}
            empty={t('report.trendScore.noHistory')}
            onRowClick={(row) => openTab?.(row.tab)}
            columns={[
              {
                key: 'kpi',
                header: t('report.detail.col.kpi'),
                render: (row) => t(`report.detail.trends.dimension.${row.labelKey}`),
              },
              { key: 'current', header: t('report.detail.trends.current'), align: 'right', render: (row) => pct(current(row.key)) },
              {
                key: 'previous',
                header: t('report.detail.trends.previous'),
                align: 'right',
                render: (row) => pct(valueOn(historyIndex.get(row.key), compareDate)),
              },
              {
                key: 'delta',
                header: 'Δ',
                align: 'right',
                render: (row) => {
                  const previous = valueOn(historyIndex.get(row.key), compareDate);
                  const value = current(row.key);
                  if (previous == null || value == null || compareDate >= today) return '—';
                  const delta = value - previous;
                  const direction = directionOf(delta);
                  const Icon = direction === 'up' ? IconArrowUpRight : direction === 'down' ? IconArrowDownRight : IconMinus;
                  return (
                    <span className={direction === 'up' ? detailStyles.good : direction === 'down' ? detailStyles.bad : detailStyles.flat}>
                      <Icon size={13} stroke={2.2} /> {signed(delta)}
                    </span>
                  );
                },
              },
              {
                key: 'trend',
                header: t('report.detail.trends.trend'),
                render: (row) => (
                  <Sparkline points={withinDays(historyIndex.get(row.key), 90)} color={row.color} width={72} />
                ),
              },
            ]}
          />
        </ChartCard>
      </div>

      <div className={detailStyles.twoColumns}>
        <ChartCard
          title={t('report.detail.trends.changes')}
          subtitle={t('report.detail.trends.changesHint')}
          action={
            <Segmented<ChangeFilter>
              value={changeFilter}
              onChange={setChangeFilter}
              options={(['all', 'positive', 'negative'] as ChangeFilter[]).map((f) => ({
                value: f,
                label: `${t(`report.detail.trends.filter.${f}`)} (${
                  changes.filter((c) => (f === 'all' ? true : f === 'positive' ? c.change.delta > 0 : c.change.delta < 0)).length
                })`,
              }))}
            />
          }
          fitContent
        >
          <DataTable
            rows={shownChanges}
            rowKey={(row) => row.key}
            empty={t('report.detail.trends.noChanges')}
            onRowClick={(row) => openTab?.(row.tab)}
            columns={[
              {
                key: 'kpi',
                header: t('report.detail.col.kpi'),
                render: (row) => t(`report.detail.trends.dimension.${row.labelKey}`),
              },
              {
                key: 'change',
                header: t('report.detail.trends.change'),
                align: 'right',
                render: (row) => (
                  <span className={row.change.delta > 0 ? detailStyles.good : detailStyles.bad}>
                    {signed(row.change.delta)}
                  </span>
                ),
              },
              {
                key: 'cause',
                header: t('report.detail.trends.cause'),
                render: (row) =>
                  row.cause
                    ? t('report.detail.trends.causeText', { kpi: kpiName(row.cause.key), delta: signed(row.cause.delta) })
                    : t('report.detail.trends.noCause'),
              },
            ]}
          />
          {changes.some((c) => c.change.partial) && (
            <p className={detailStyles.note}>{t('report.detail.trends.partialHistory')}</p>
          )}
        </ChartCard>

        <ChartCard
          title={t('report.detail.trends.phaseTrends')}
          subtitle={t('report.detail.trends.phaseTrendsHint')}
          action={
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              {phaseTrend !== 'status' && (
                <select
                  value={phaseId}
                  onChange={(e) => setPhaseId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className={detailStyles.segment}
                >
                  <option value="all">{t('report.detail.trends.allPhases')}</option>
                  {[...phaseNames.entries()].map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              )}
              <Segmented<PhaseRange>
                value={phaseRange}
                onChange={setPhaseRange}
                options={[
                  { value: '28', label: t('report.detail.range.weeks4') },
                  { value: '84', label: t('report.detail.range.weeks12') },
                  { value: '182', label: t('report.detail.range.months6') },
                  { value: '365', label: t('report.detail.range.months12') },
                ]}
              />
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
            <Segmented<PhaseTrend>
              value={phaseTrend}
              onChange={setPhaseTrend}
              options={[
                { value: 'progress', label: t('report.detail.trends.tr01') },
                { value: 'qa', label: t('report.detail.trends.tr02') },
                { value: 'status', label: t('report.detail.trends.tr03') },
              ]}
            />
            {phaseDates.length > 0 && phaseDates.length < FEW_POINTS && (
              <p className={detailStyles.note}>
                {t('report.detail.trends.fewPoints', {
                  count: phaseDates.length,
                  dates: phaseDates.map((d) => formatDay(d)).join(', '),
                })}
              </p>
            )}
            <div style={{ flex: 1, minHeight: 0 }}>
              {phaseSeries.some((s) => s.points.length > 0) ? (
                <MultiLineChart series={phaseSeries} />
              ) : (
                <p className={styles.planDescription}>{t('report.trendScore.noHistory')}</p>
              )}
            </div>
          </div>
        </ChartCard>
      </div>

      <ActionsHint text={t('report.detail.actionsHint.trends', { count: trendActions })} onOpen={() => openTab?.('actions')} />
    </div>
  );
};

export default Trends;
